// src/App.js
import React, { useState, useEffect } from "react";
import { Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axiosInstance from "./axiosInstance";

import MainDashboard from "./components/MainDashboard";
import TeacherManager from "./components/TeacherManager";
import StudentManager from "./components/StudentManager";
import SessionLogger from "./components/SessionLogger";
import Login from "./components/Login";
import ChangePassword from "./components/ChangePassword";
import DarkModeToggle from "./components/DarkModeToggle";
import AdminFinancialEntry from "./components/AdminFinancialEntry";
import DataExport from "./components/DataExport";
import InvoiceGenerator from "./components/InvoiceGenerator";

import './App.css';

function App() {
  const [user, setUser] = useState(null);
  const [darkMode, setDarkMode] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  // Check token on page load to automatically set user
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem("jwtToken");

      if (token && token.trim()) {
        axiosInstance.defaults.headers.common["Authorization"] = `Bearer ${token}`;

        try {
          const decodedToken = JSON.parse(atob(token.split('.')[1]));

          // Check if token is expired
          const currentTime = Date.now() / 1000;
          if (decodedToken.exp && decodedToken.exp < currentTime) {
            // Token expired
            localStorage.removeItem("jwtToken");
            delete axiosInstance.defaults.headers.common["Authorization"];
            setUser(null);
          } else {
            // Token valid
            setUser({
              name: decodedToken.name,
              role: decodedToken.role,
              id: decodedToken.id,
              isNewUser: decodedToken.isNewUser || false,
            });
          }
        } catch (error) {
          console.error("Error decoding token:", error);
          localStorage.removeItem("jwtToken");
          delete axiosInstance.defaults.headers.common["Authorization"];
          setUser(null);
        }
      }

      setIsCheckingAuth(false);
    };

    checkAuth();
  }, []);

  // Handle logout
  const handleLogout = () => {
    setUser(null);
    localStorage.removeItem("jwtToken");
    delete axiosInstance.defaults.headers.common["Authorization"];
    navigate("/");
  };

  // Show loading while checking authentication
  if (isCheckingAuth) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontSize: '1.2rem'
      }}>
        Loading...
      </div>
    );
  }

  // Redirect new users to change password (but not if already there)
  if (user?.isNewUser && location.pathname !== "/change-password") {
    return <Navigate to="/change-password" replace />;
  }

  return (
    <div className={`app-container ${darkMode ? 'dark-mode' : ''}`}>
      {/* Navigation */}
      {user && (
        <nav className="top-nav">
          <button onClick={() => navigate('/dashboard')}>Dashboard</button>
          <button onClick={() => navigate('/logs')}>Log Session</button>

          {user.role === 'admin' && (
            <>
              <button onClick={() => navigate('/finance-entry')}>AR/AP</button>
              <button onClick={() => navigate('/students')}>Students</button>
              <button onClick={() => navigate('/teachers')}>Teachers</button>
              <button onClick={() => navigate('/invoices')}>Invoices</button>
              <button onClick={() => navigate('/export')}>Export</button>
            </>
          )}

          <button onClick={() => navigate('/change-password')}>Change Password</button>

          <button className="logout-btn" onClick={handleLogout}>
            Logout
          </button>

          <DarkModeToggle />
        </nav>
      )}

      {/* Routes */}
      <Routes>
        <Route
          path="/"
          element={!user ? <Login setUser={setUser} /> : <Navigate to="/dashboard" replace />}
        />

        <Route
          path="/change-password"
          element={user ? <ChangePassword user={user} setUser={setUser} /> : <Navigate to="/" replace />}
        />

        {/* Protected Routes */}
        {user && (
          <>
            <Route path="/dashboard" element={<MainDashboard user={user} />} />
            <Route
              path="/teachers"
              element={user.role === 'admin' ? <TeacherManager /> : <Navigate to="/dashboard" replace />}
            />
            <Route
              path="/students"
              element={user.role === 'admin' ? <StudentManager /> : <Navigate to="/dashboard" replace />}
            />
            <Route path="/logs" element={<SessionLogger user={user} />} />
            <Route
              path="/finance-entry"
              element={user.role === 'admin' ? <AdminFinancialEntry /> : <Navigate to="/dashboard" replace />}
            />
            <Route
              path="/export"
              element={user.role === 'admin' ? <DataExport user={user} /> : <Navigate to="/dashboard" replace />}
            />
            <Route
              path="/invoices"
              element={user.role === 'admin' ? <InvoiceGenerator user={user} /> : <Navigate to="/dashboard" replace />}
            />
          </>
        )}

        {/* Catch-all for unauthorized users */}
        <Route path="*" element={!user ? <Navigate to="/" replace /> : <Navigate to="/dashboard" replace />} />
      </Routes>
    </div>
  );
}

export default App;